import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, catchError, delay, map, of, Subject, takeUntil, tap, throwError } from 'rxjs';
import { Venue } from 'src/app/models/Venue';
import { ApiService } from 'src/app/services/api.service';
import { NavigationService, NAVIGATIONURLS } from 'src/app/services/navigation.service';
import { ManagerFacade } from 'src/app/store/manager/+state/manager.facade';
import { DeliveryProvider, PrepTimeInputDialogComponent } from '../prep-time-input-dialog/prep-time-input-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AskPermissionDialogComponent } from '../ask-permission-dialog/ask-permission-dialog.component';
import { AuthFacade } from 'src/app/store/auth-state/+state/auth.facade';
import { TranslateService } from '@ngx-translate/core';
import { PreorderType } from 'src/app/models/PreorderType';
import { SmoothrInputOffsetsDialogComponent } from '../smoothr-input-offsets-dialog/smoothr-input-offsets-dialog.component';
import Offset from 'src/app/models/Offset';
import { TimeUtils } from '../../utils/time-utils';
import { AskSnoozingDialogComponent } from '../ask-snoozing-dialog/ask-snoozing-dialog.component';
import { AppSettingsService } from 'src/app/services/app-settings.service';

@Component({
	selector: 'app-admin-console-options',
	templateUrl: './admin-console-options.component.html',
	styleUrl: './admin-console-options.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminConsoleOptionsComponent implements OnInit, OnDestroy {
	public isWoltActiveInVenue$ = new BehaviorSubject<boolean>(false);
	public isUberEatsActiveInVenue$ = new BehaviorSubject<boolean>(false);
	public isDeliverooActiveInVenue$ = new BehaviorSubject<boolean>(false);

	public urlLinkEnum = NAVIGATIONURLS;
	public statusUber$ = this.managerFacade.statusUber$;
	public statusWolt$ = this.managerFacade.statusWolt$;
	public statusDeliveroo$ = this.managerFacade.statusDeliveroo$;
	public venue$ = this.managerFacade.venue$;
	public venueValue$ = new BehaviorSubject<Venue | null>(null);
	public deliveryProvider = DeliveryProvider;
	private readonly onDestroy$ = new Subject<void>();
	public preorderTypeEnum = PreorderType;
	public isVenueOpen$ = new BehaviorSubject<boolean>(false);
	music$ = this.appSettingsService.appSettings$.pipe(map(s => s.isSoundTurnOn));
	refund$ = this.appSettingsService.appSettings$.pipe(map(s => s.isShowRefundOn));
	discount$ = this.appSettingsService.appSettings$.pipe(map(s => s.isShowDiscountOn));

	constructor(
		private managerFacade: ManagerFacade,
		private authFacade: AuthFacade,
		private apiService: ApiService,
		private dialog: MatDialog,
		private navService: NavigationService,
		private translate: TranslateService,
		private appSettingsService: AppSettingsService
	) {}

	ngOnInit() {
		this.venue$.pipe(takeUntil(this.onDestroy$)).subscribe(venue => {
			if (venue) {
				this.venueValue$.next(venue);
				this.isVenueOpen$.next(!!this.isVenueOpen(venue));
			}
			this.venueValue$.next(venue);

			if (venue?.externalOrderProviders) {
				const { uberEats, wolt } = venue.externalOrderProviders;
				if (venue?._id) {
					if (uberEats.enabled) {
						this.managerFacade.statusUber(venue._id);
					}
					if (wolt.enabled) {
						this.managerFacade.statusWolt(venue._id);
					}
					this.managerFacade.statusDeliveroo(venue._id);
				}
			}
		});

		this.statusWolt$
			.pipe(
				tap(value => {
					this.isWoltActiveInVenue$.next(value?.isActive ?? false);
				}),
				takeUntil(this.onDestroy$)
			)
			.subscribe();

		this.statusUber$
			.pipe(
				tap(value => {
					this.isUberEatsActiveInVenue$.next(value?.isActive ?? false);
				}),
				takeUntil(this.onDestroy$)
			)
			.subscribe();

		this.statusDeliveroo$.pipe(takeUntil(this.onDestroy$)).subscribe(val => {
			this.isDeliverooActiveInVenue$.next(val?.isActive ?? false);
		});
	}

	ngOnDestroy(): void {
		this.onDestroy$.next();
	}

	updateUber() {
		const venue = this.venueValue$.getValue();
		if (venue) {
			const valueOfActive = !this.isUberEatsActiveInVenue$.getValue()
				? this.translate.instant('admin_console.ask_permissions_eats_online')
				: this.translate.instant('admin_console.ask_permissions_eats_offline');
			const dialogRef = this.dialog.open(AskSnoozingDialogComponent, {
				data: {
					text: valueOfActive,
					isActive: this.isUberEatsActiveInVenue$.getValue(),
				},
			});

			dialogRef.afterClosed().subscribe((result: { value: boolean; snoozed: string }) => {
				if (result && result?.value) {
					this.deactivateActivateServiceUber(venue?._id, !this.isUberEatsActiveInVenue$.getValue(), result?.snoozed ?? '')
						.pipe(
							delay(500),

							tap(value => {
								this.managerFacade.statusUber(venue._id);
								this.managerFacade.snoozedUber(result.snoozed);
							}),
							takeUntil(this.onDestroy$)
						)
						.subscribe();
				}
			});
		}
	}

	updateWolt() {
		const venue = this.venueValue$.getValue();
		if (venue) {
			const valueOfActive = !this.isWoltActiveInVenue$.getValue()
				? this.translate.instant('admin_console.ask_permissions_wolt_online')
				: this.translate.instant('admin_console.ask_permissions_wolt_offline');
			const dialogRef = this.dialog.open(AskSnoozingDialogComponent, {
				data: {
					text: valueOfActive,
					isActive: this.isWoltActiveInVenue$.getValue(),
				},
			});
			dialogRef.afterClosed().subscribe((result: { value: boolean; snoozed: string }) => {
				if (result && result?.value) {
					this.deactivateActivateServiceWolt(venue?._id, !this.isWoltActiveInVenue$.getValue(), result?.snoozed ?? '')
						.pipe(
							delay(500),
							tap(value => {
								this.managerFacade.statusWolt(venue._id);
								this.managerFacade.snoozedWolt(result.snoozed);
							}),
							takeUntil(this.onDestroy$)
						)
						.subscribe();
				}
			});
		}
	}

	updateDeliveroo() {
		const venue = this.venueValue$.getValue();
		if (!venue) return;

		const valueOfActive = !this.isDeliverooActiveInVenue$.getValue()
			? this.translate.instant('admin_console.ask_permissions_deliveroo_online')
			: this.translate.instant('admin_console.ask_permissions_deliveroo_offline');
		const dialogRef = this.dialog.open(AskSnoozingDialogComponent, {
			data: {
				text: valueOfActive,
				isActive: this.isDeliverooActiveInVenue$.getValue(),
			},
		});
		dialogRef.afterClosed().subscribe((result: { value: boolean; snoozed: string }) => {
			if (result && result?.value) {
				this.deactivateActivateServiceDeliveroo(venue?._id, !this.isDeliverooActiveInVenue$.getValue(), result?.snoozed ?? '')
					.pipe(
						delay(500),
						tap(value => {
							this.managerFacade.statusDeliveroo(venue._id);
							this.managerFacade.snoozedDeliveroo(result.snoozed);
						}),
						takeUntil(this.onDestroy$)
					)
					.subscribe();
			}
		});
	}

	private deactivateActivateServiceDeliveryPickupAndOwnDelivery(
		venue: Venue,
		enabled: boolean,
		snoozed: string,
		preorderType: PreorderType
	) {
		return this.apiService.updateVenueDeliveryPickupAndOwnDelivery(venue, enabled, snoozed, preorderType).pipe(
			map(response => {
				console.log(response);
				if (response.status === 200 && response.body) {
					return { venue: JSON.parse(response.body) as Venue };
				} else {
					throw new Error(`Unexpected status: ${response.status}`);
				}
			}),
			catchError(error => {
				return throwError(() => new Error(`Request failed with status ${error.status || 500}`));
			})
		);
	}

	private deactivateActivateServiceWolt(venueId: string, status: boolean, snoozed: string) {
		return this.apiService.activateDeactivateActivateWolt(venueId, status, snoozed).pipe(
			map(response => {
				if (response.status === 200) {
					return { status: response.status };
				} else {
					throw new Error(`Unexpected status: ${response.status}`);
				}
			}),
			catchError(error => {
				return of({ status: error.status || 500 });
			})
		);
	}

	private deactivateActivateServiceUber(venueId: string, status: boolean, snoozed: string) {
		return this.apiService.activateDeactivateActivateUber(venueId, status, snoozed).pipe(
			map(response => {
				console.log(response);
				if (response.status === 200) {
					return { status: response.status };
				} else {
					throw new Error(`Unexpected status: ${response.status}`);
				}
			}),
			catchError(error => {
				return of({ status: error.status || 500 });
			})
		);
	}

	private deactivateActivateServiceDeliveroo(venueId: string, status: boolean, snoozed: string) {
		return this.apiService.activateDeactivateActivateDeliveroo(venueId, status, snoozed).pipe(
			map(response => {
				if (response.status === 200) {
					return { status: response.status };
				} else {
					throw new Error(`Unexpected status: ${response.status}`);
				}
			}),
			catchError(error => {
				return of({ status: error.status || 500 });
			})
		);
	}

	async openDialogSetTime(type: DeliveryProvider) {
		const venue = this.venueValue$.getValue();
		if (!venue) {
			return;
		}
		const dialogRef = this.dialog.open(PrepTimeInputDialogComponent, {
			data: {
				type: type,
				value: venue.externalOrderProviders,
				venueId: venue._id,
			},
		});

		dialogRef.afterClosed().subscribe(result => {
			console.log(result);
			// if (venue._id) {
			// 	this.managerFacade.loadVenue({ venueId: venue._id });
			// }
		});
	}

	updateSmoothrWithParams(venue: Venue, preorderType: PreorderType) {
		if (!venue) {
			return;
		}
		const copyVenue = JSON.parse(JSON.stringify(venue)) as Venue;
		if (preorderType === PreorderType.DELIVERY) {
			copyVenue.deliveryEnabled = !copyVenue.deliveryEnabled;
		} else if (preorderType == PreorderType.TAKE_AWAY) {
			copyVenue.preorderTakeAway = !copyVenue.preorderTakeAway;
		} else {
			return;
		}
		let text = '';
		let dataIsActive: any = true;
		if (preorderType === PreorderType.DELIVERY) {
			copyVenue.deliveryEnabled ? (dataIsActive = false) : (dataIsActive = true);
			text = copyVenue.deliveryEnabled
				? this.translate.instant('admin_console.ask_permissions_self_delivery_online')
				: this.translate.instant('admin_console.ask_permissions_self_delivery_offline');
		} else if (preorderType === PreorderType.TAKE_AWAY) {
			copyVenue.preorderTakeAway ? (dataIsActive = false) : (dataIsActive = true);
			text = copyVenue.preorderTakeAway
				? this.translate.instant('admin_console.ask_permissions_self_take_away_online')
				: this.translate.instant('admin_console.ask_permissions_self_take_away_offline');
		}
		console.log(copyVenue.preorderTakeAway);
		const dialogRef = this.dialog.open(AskSnoozingDialogComponent, {
			data: {
				text: text,
				isActive: dataIsActive,
				//isActive: this.isVenueOpen$.getValue()
			},
		});
		dialogRef.afterClosed().subscribe((result: { value: boolean; snoozed: string }) => {
			if (result && result?.value) {
				this.deactivateActivateServiceDeliveryPickupAndOwnDelivery(
					copyVenue,
					result.snoozed ? false : true,
					result.snoozed,
					preorderType
				).subscribe({
					next: ({ venue }) => {
						this.apiService
							.updateVenue(venue)
							.pipe(takeUntil(this.onDestroy$))
							.subscribe(result => {
								if (result && result?._id) {
									this.managerFacade.updateVenue(result);
								}
								console.log('VEnue Result', result);
							});
					},
					error: e => {
						console.error(e);
					},
				});
			}
		});
	}

	logout() {
		const dialogRef = this.dialog.open(AskPermissionDialogComponent, {
			data: {
				text: this.translate.instant('admin_console.logout_question'),
			},
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.authFacade.logout();
				this.navService.login();
			}
		});
	}

	public updateSmoothrOffsetsType() {
		const copyVenue = JSON.parse(JSON.stringify(this.venueValue$.getValue())) as Venue;
		if (!copyVenue) {
			return;
		}
		const dialogRef = this.dialog.open(SmoothrInputOffsetsDialogComponent, {
			data: {
				venue: copyVenue,
			},
		});
		dialogRef.afterClosed().subscribe((result: { deliveryOffset: Offset; take_awayOffset: Offset }) => {
			if (result) {
				this.updateOffsetWithQuery(copyVenue, result);
			}
		});
	}

	private updateOffsetWithQuery(
		venue: Venue,
		result: {
			deliveryOffset: Offset;
			take_awayOffset: Offset;
		}
	) {
		if (!venue) {
			return;
		}
		venue.offsets.preorder.takeAway = result.take_awayOffset;
		venue.offsets.preorder.delivery = result.deliveryOffset;

		this.apiService
			.updateVenue(venue)
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(result => {
				if (result && result?._id) {
					this.venueValue$.next(result);
				}
				console.log('VEnue Result', result);
			});
	}

	isVenueOpen = (venue: Venue) => {
		return (
			venue &&
			venue.isServiceActivated &&
			venue.openingHours &&
			venue.openingHours?.length > 0 &&
			TimeUtils.doesHoursMatchNow(venue.openingHours)
		);
	};

	turnOnMusic() {
		this.appSettingsService.toggleMusic();
	}

	toggleShowRefund() {
		this.appSettingsService.toggleShowRefund();
	}

	toggleShowDiscount() {
		this.appSettingsService.toggleShowDiscount();
	}

	public checkIfSetupOrderProviders(venue: Venue, orderProvider: string) {
		if (orderProvider === 'wolt') {
			return !!venue.externalOrderProviders?.wolt?.venue;
		}
		if (orderProvider === 'self-delivery') {
			return !!(venue?.deliveryHours ?? []).length;
		}
		if (orderProvider === 'uber_eat') {
			return !!venue.externalOrderProviders?.uberEats?.storeId;
		}
		if (orderProvider === 'deliveroo') {
			return !!venue.externalOrderProviders.deliveroo.siteId;
		}

		return true;
	}

	hasDeliveryHours(venue) {
		if (!venue) {
			return false;
		}
		return venue.deliveryHours && venue.deliveryHours.length > 0;
	}

	refreshPage() {
		window.location.reload();
	}

	// isInsideListOfVenues() {
	// 	const listOfReadableIds = [
	// 		'losteria_herne',
	// 		'losteria_brunnthal',
	// 		'losteria_hanau'
	// 	];
	// 	const isInsideList = listOfReadableIds.includes(
	// 		this.venueValue$.getValue()?.readableId || ''
	// 	);
	// 	const isProd = environment.production;

	// 	return isInsideList || !isProd;
	// }
}
